/*
 * Coffee page behaviour (PurchasePanel.tsx + BrewGuides.tsx).
 *   [data-product-panel]  pack radios (the form's "id"), quantity −/+, the Add to cart label
 *                         (price × quantity, sold out, "Added"), and ?variant= in the URL.
 *                         Adding to the cart itself is champions.js (form[data-product-form]).
 *   [data-brew-guides]    the "Other ways to brew it" tabs.
 */
(() => {
  const MAX_QTY = 20;

  // Prices as snippets/price.liquid prints them: the dirham sign + "85.00" for AED, otherwise the shop's money format.
  function formatMoney(cents) {
    const fmt = (window.Champions && window.Champions.moneyFormat) || "{{amount}}";
    const fixed = (n, d, t = ",", dec = ".") => {
      const [i, f] = (n / 100).toFixed(d).split(".");
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, t) + (f ? dec + f : "");
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => {
      if (k === "amount_no_decimals") return fixed(cents, 0);
      if (k === "amount_with_comma_separator") return fixed(cents, 2, ".", ",");
      if (k === "amount_no_decimals_with_comma_separator") return fixed(cents, 0, ".", ",");
      if (k === "amount_with_apostrophe_separator") return fixed(cents, 2, "'", ".");
      return fixed(cents, 2);
    });
  }
  function setPrice(el, cents) {
    if (!el) return;
    const outer = el.firstElementChild || el;
    if ((window.Champions && window.Champions.currency) === "AED" && outer.lastElementChild) {
      outer.lastElementChild.textContent = (cents / 100).toFixed(2);
    } else {
      outer.textContent = formatMoney(cents);
    }
  }

  function setupPanel(panel) {
    if (panel.__product) return;
    panel.__product = true;
    const form = panel.querySelector("form");
    if (!form) return;
    const packs = Array.from(panel.querySelectorAll("[data-pack]"));
    const qtyInput = panel.querySelector("[data-qty-input]");
    const qtyDisplay = panel.querySelector("[data-qty-display]");
    const button = panel.querySelector("[data-add-button]");
    const priceEl = panel.querySelector("[data-add-price]");
    const states = Object.fromEntries(Array.from(panel.querySelectorAll("[data-add-state]")).map((s) => [s.dataset.addState, s]));
    let qty = 1;
    let addedTimer = 0;
    let pending = false;

    const selected = () => form.querySelector('input[name="id"]:checked') || form.querySelector('input[name="id"]');
    const show = (name) => Object.entries(states).forEach(([k, el]) => (el.hidden = k !== name));

    const paint = ({ url = false } = {}) => {
      const input = selected();
      if (!input) return;
      const available = input.dataset.available === "true";
      const price = Number(input.dataset.price || 0);
      packs.forEach((label) => {
        const on = label.querySelector("input") === input;
        label.classList.remove(...(on ? label.dataset.off : label.dataset.on).split(" ").filter(Boolean));
        label.classList.add(...(on ? label.dataset.on : label.dataset.off).split(" ").filter(Boolean));
      });
      if (qtyInput) qtyInput.value = String(qty);
      if (qtyDisplay) qtyDisplay.textContent = String(qty);
      setPrice(priceEl, price * qty);
      if (button) button.disabled = !available;
      clearTimeout(addedTimer);
      show(available ? "add" : "sold-out");
      if (url && input.type === "radio") {
        const u = new URL(window.location.href);
        u.searchParams.set("variant", input.value);
        window.history.replaceState(window.history.state, "", u);
      }
    };

    form.addEventListener("change", (e) => {
      if (e.target.name === "id") paint({ url: true });
    });
    panel.querySelectorAll("[data-qty-step]").forEach((b) =>
      b.addEventListener("click", () => {
        qty = Math.max(1, Math.min(MAX_QTY, qty + Number(b.dataset.qtyStep)));
        paint();
      }),
    );

    // "Added" for a moment once champions.js has put the pack in the cart.
    form.addEventListener("submit", () => {
      pending = true;
    });
    document.addEventListener("champions:cart-updated", () => {
      if (!pending) return;
      pending = false;
      if (!states.added || (button && button.disabled)) return;
      show("added");
      clearTimeout(addedTimer);
      addedTimer = setTimeout(() => show(selected() && selected().dataset.available === "true" ? "add" : "sold-out"), 2200);
    });
    const err = panel.querySelector("[data-product-error]");
    if (err) new MutationObserver(() => err.textContent && (pending = false)).observe(err, { childList: true, characterData: true, subtree: true });

    paint();
  }

  function setupGuides(root) {
    if (root.__brew) return;
    root.__brew = true;
    const tabs = Array.from(root.querySelectorAll("[data-brew-tab]"));
    const panels = Array.from(root.querySelectorAll("[data-brew-panel]"));
    const select = (id, focus) => {
      tabs.forEach((t) => {
        const on = t.dataset.brewTab === id;
        t.setAttribute("aria-selected", String(on));
        t.tabIndex = on ? 0 : -1;
        t.classList.remove(...(on ? t.dataset.off : t.dataset.on).split(" ").filter(Boolean));
        t.classList.add(...(on ? t.dataset.on : t.dataset.off).split(" ").filter(Boolean));
        const pill = t.querySelector("[data-brew-pill]");
        if (pill) pill.hidden = !on;
        if (on && focus) t.focus();
      });
      panels.forEach((p) => {
        const on = p.dataset.brewPanel === id;
        if (on && p.hidden) {
          p.classList.remove("rise-in");
          void p.offsetWidth;
          p.classList.add("rise-in");
        }
        p.hidden = !on;
      });
    };
    tabs.forEach((t, i) => {
      t.tabIndex = t.getAttribute("aria-selected") === "true" ? 0 : -1;
      t.addEventListener("click", () => select(t.dataset.brewTab));
      t.addEventListener("keydown", (e) => {
        const d = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
        if (!d) return;
        e.preventDefault();
        select(tabs[(i + d + tabs.length) % tabs.length].dataset.brewTab, true);
      });
    });
  }

  const init = (scope = document) => {
    scope.querySelectorAll("[data-product-panel]").forEach(setupPanel);
    scope.querySelectorAll("[data-brew-guides]").forEach(setupGuides);
  };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => init());
  else init();
  document.addEventListener("shopify:section:load", (e) => init(e.target));
})();
